/**
 * Playbook side of "Personnel on formations": which option lists carry a
 * default personnel group (`personnel_group_id`), which list it is picked
 * from, and how a personnel option reads in the picker and the row badge.
 *
 * The tag points at a PERSONNEL option on the SAME side as the formation
 * (our formations → our personnel; the opponent's formations → theirs). Play
 * Entry pre-fills the personnel card from it — see
 * `play-entry/formation-personnel.ts`.
 */
import type { StatCategory, StatOption } from '../../lib/api-types';

/** Stats whose options carry a default personnel (mirrors the playbook service). */
export const PERSONNEL_DEFAULT_CATEGORIES: readonly StatCategory[] = ['FORMATION'];

/** The stat the default personnel is picked from. */
export const PERSONNEL_CATEGORY: StatCategory = 'PERSONNEL';

/** User-visible strings for the personnel picker and badge (one place for translation). */
export const DEFAULT_PERSONNEL_COPY = {
  label: 'Default personnel',
  none: 'None — pick personnel on each play',
  loading: 'Loading personnel…',
  unavailable: 'The personnel list could not be loaded. Try again later.',
  empty: 'No personnel groups yet — add them on the Personnel tab.',
  hint: 'Picking this formation on Play Entry fills in Personnel. The operator can still change it.',
  disabled: '(disabled)',
  badgeTitle: (text: string): string => `Default personnel on Play Entry: ${text}`,
  badge: (value: string): string => `${value} pers.`,
} as const;

export function hasDefaultPersonnel(category: StatCategory): boolean {
  return PERSONNEL_DEFAULT_CATEGORIES.includes(category);
}

/** "12 · 1 RB, 2 TE, 2 WR" — the code leads because coaches call personnel by number. */
export function personnelOptionText(option: Pick<StatOption, 'value' | 'label'>): string {
  const label = option.label.trim();
  const value = option.value.trim();
  if (label === '' || label.toLowerCase() === value.toLowerCase()) return value || label;
  return label.toLowerCase().includes(value.toLowerCase()) ? label : `${value} · ${label}`;
}

/** The personnel option a formation is tagged with, or `null` (untagged / not in the list). */
export function findDefaultPersonnel(
  formation: Pick<StatOption, 'personnel_group_id'>,
  personnel: readonly StatOption[] | null | undefined
): StatOption | null {
  const id = formation.personnel_group_id ?? null;
  if (id === null || !personnel) return null;
  return personnel.find((option) => option.option_id === id) ?? null;
}
